import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  getAssignedPromoters, getSalesByMonthForPromoters,
  getTargetsForPromoters, getPendingSalesByPromoters, approveSale, rejectSale,
  getCancelRequests, approveCancelSale, rejectCancelSale, updateSaleHighend,
  revertSaleApproval, getSalesBySerials,
} from "../../firebase/services";
import { calcSalesBonus, calcHighendBonus } from "../../firebase/schema";
import { isMonthEditable } from "../promoter/PromoterSales";
import { useSalesSort } from "../../hooks/useSalesSort";
import { format } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";
import { usePayrollConfig } from "../../hooks/usePayrollConfig";

export default function SupervisorSales() {
  const { t } = useLanguage();
  const { user, profile } = useAuth();
  const [tab, setTab] = useState("progress");
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [promoters, setPromoters] = useState([]);
  const [sales, setSales] = useState([]);
  const [targets, setTargets] = useState({});
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const { cfgFor } = usePayrollConfig();
  const bonusCfg = cfgFor(month).bonus;   // quy tắc thưởng áp dụng cho tháng đang xem
  const [cancelReqs, setCancelReqs] = useState([]);
  const [dupMap, setDupMap] = useState({}); // serial → [đơn khác cùng serial]

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const proms = await getAssignedPromoters(user.uid);
    const ids = proms.map(p => p.uid);
    const [s, t, p, cancels] = await Promise.all([
      getSalesByMonthForPromoters(month, ids),
      getTargetsForPromoters(month, ids),
      getPendingSalesByPromoters(ids),
      getCancelRequests(ids),
    ]);
    const tm = {};
    t.forEach(x => { tm[x.promoterId] = x; });
    setPromoters(proms);
    setSales(s);
    setTargets(tm);
    setPending(p);
    setCancelReqs(cancels);
    if (p.length > 0) {
      getSalesBySerials(p.map(x => x.serial), ids).then(setDupMap).catch(() => setDupMap({}));
    } else {
      setDupMap({});
    }
    setLoading(false);
  };

  const handleApproveCancel = async (s) => { await approveCancelSale(s.id, profile?.name || "Supervisor", "supervisor"); load(); };
  // Từ chối hủy không đụng tới số liệu nên luôn cho phép, kể cả tháng đã khóa
  const handleRejectCancel = async (s) => { await rejectCancelSale(s.id, profile?.name || "Supervisor", "supervisor"); load(); };

  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const filteredDetail = q
    ? sales.filter(s =>
        (s.promoterName || "").toLowerCase().includes(q) ||
        (s.model || "").toLowerCase().includes(q) ||
        (s.serial || "").toLowerCase().includes(q))
    : sales;
  const { sorted: sortedDetail, toggle: sortToggle, arrow: sortArrow } = useSalesSort(filteredDetail);

  const handleToggleHighend = async (s) => {
    await updateSaleHighend(s.id, !s.isHighend, profile?.name || "Supervisor", "supervisor");
    load();
  };

  const handleRevertApproval = async (s) => {
    if (!window.confirm(t("sales.revert_confirm"))) return;
    await revertSaleApproval(s.id, profile?.name || "Supervisor", "supervisor");
    load();
  };

  const STATUS_VI = { approved: "đã duyệt", pending: "chờ duyệt", rejected: "bị từ chối", cancelled: "đã hủy" };
  const dupOf = (s) => (dupMap[s.serial] || []).filter(d => d.id !== s.id);

  useEffect(() => { load(); }, [user, month]);

  const getStats = (uid) => {
    const sold = sales.filter(s => s.promoterId === uid).length;
    const highendSold = sales.filter(s => s.promoterId === uid && s.isHighend).length;
    const t = targets[uid];
    const target = t?.sellTarget || 0;
    const highendTarget = t?.highendTarget || 0;
    const hitRate = target > 0 ? sold / target : 0;
    const heHitRate = highendTarget > 0 ? highendSold / highendTarget : 0;
    return { sold, highendSold, target, highendTarget, hitRate, heHitRate,
      bonus: calcSalesBonus(sold, target, bonusCfg?.salesTiers),
      heBonus: calcHighendBonus(highendSold, highendTarget, bonusCfg?.highendConfig) };
  };

  const hitBadge = (r) => r >= 1.0 ? "badge-blue" : r >= 0.8 ? "badge-green" : "badge-red";

  const handleApprove = async (s) => {
    await approveSale(s.id, profile?.name || "Supervisor", "supervisor");
    load();
  };
  const handleReject = async (s) => { await rejectSale(s.id, profile?.name || "Supervisor", "supervisor"); load(); };
  const exportSalesCSV = () => {
    const promoterMap = Object.fromEntries(promoters.map(p => [p.uid, p]));
    const header = ["Tháng", "Promoter", "Shop", "Mã model", "Số serial", "Cao cấp", "Ngày bán", "Người duyệt", "Vai trò"];
    const rows = sales.map(s => [
      s.month, s.promoterName,
      promoterMap[s.promoterId]?.shopName || "",
      s.model, s.serial,
      s.isHighend ? "Có" : "Không",
      s.saleDate, s.approvedBy || "", s.approverRole || "",
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `doanh-so_${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleApproveAll = async () => {
    if (pending.length === 0) return;
    await Promise.all(pending.map(s => approveSale(s.id, profile?.name || "Supervisor", "supervisor")));
    load();
  };

  return (
    <>
      <div className="page-header">
        <div><h1>{t("sales.title")}</h1><p>{t("sales.subtitle")}</p></div>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 160 }} />
      </div>

      <div className="tabs">
        {[["progress", t("sales.tab_progress")], ["pending", `${t("sales.tab_pending")}${pending.length > 0 ? ` (${pending.length})` : ""}`], ["cancel", `${t("sales.tab_cancel")}${cancelReqs.length > 0 ? ` (${cancelReqs.length})` : ""}`], ["detail", t("sales.tab_detail")]].map(([k, l]) => (
          <div key={k} className={"tab" + (tab === k ? " active" : "")} onClick={() => setTab(k)}>{l}</div>
        ))}
      </div>

      {loading && <div style={{ color: "#888", padding: 20 }}>{t("common.loading")}</div>}

      {!loading && tab === "progress" && (
        <div className="card">
          <table className="table-card">
            <thead>
              <tr>
                <th>{t("sales.promoter")}</th>
                <th>{t("sales.target_ds")}</th><th>{t("sales.sold")}</th><th>{t("sales.hit_rate")}</th>
                <th>{t("sales.target_he")}</th><th>{t("sales.he_sold")}</th>
                <th>{t("sales.bonus_ds")}</th><th>{t("sales.bonus_he")}</th>
              </tr>
            </thead>
            <tbody>
              {promoters.map(p => {
                const s = getStats(p.uid);
                const pct = s.target > 0 ? Math.min(100, Math.round(s.hitRate * 100)) : 0;
                return (
                  <tr key={p.uid}>
                    <td data-label="Promoter"><strong>{p.name}</strong><div style={{ fontSize: 11, color: "#888" }}>{p.shopName}</div></td>
                    <td data-label="Target DS">{s.target || <span style={{ color: "#bbb" }}>—</span>}</td>
                    <td data-label="Đã bán">{s.sold}</td>
                    <td data-label="Hit rate"><span className={"badge " + hitBadge(s.hitRate)}>{pct}%</span></td>
                    <td data-label="Target HE">{s.highendTarget || <span style={{ color: "#bbb" }}>—</span>}</td>
                    <td data-label="HE đã bán">
                      <span style={{ fontWeight: 500, color: s.highendSold >= s.highendTarget && s.highendTarget > 0 ? "#C8152A" : undefined }}>
                        {s.highendSold}
                      </span>
                      {s.highendTarget > 0 && (
                        <span style={{ fontSize: 11, color: "#aaa", marginLeft: 4 }}>/ {s.highendTarget}</span>
                      )}
                    </td>
                    <td data-label="Thưởng DS">{s.bonus > 0 ? s.bonus.toLocaleString("vi-VN") + " đ" : <span className="badge badge-gray">{t("sales.not_reached")}</span>}</td>
                    <td data-label="Thưởng HE">{s.heBonus > 0 ? s.heBonus.toLocaleString("vi-VN") + " đ" : "—"}</td>
                  </tr>
                );
              })}
              {promoters.length === 0 && <tr><td colSpan={8} style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("sv_dash.no_promoters")}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {!loading && tab === "pending" && (
        <div className="card">
          {pending.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button className="btn btn-primary btn-sm" onClick={handleApproveAll}>
                ✓ {t("sales.approve_all")} ({pending.length})
              </button>
            </div>
          )}
          {pending.length === 0 ? (
            <div style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("sales.no_pending")}</div>
          ) : (
            <table className="table-card">
              <thead><tr><th>{t("sales.promoter")}</th><th>{t("sales.model")}</th><th>{t("sales.serial")}</th><th>{t("sales.highend")}</th><th>{t("sales.sale_date")}</th><th></th></tr></thead>
              <tbody>
                {pending.map(s => (
                  <tr key={s.id}>
                    <td data-label="Promoter">{s.promoterName}</td>
                    <td data-label="Model">{s.model}</td>
                    <td data-label="Serial" style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {s.serial}
                      {dupOf(s).length > 0 && (
                        <div style={{ fontFamily: "inherit", fontSize: 11, color: "#B91C1C", marginTop: 4, fontWeight: 600 }}>
                          {t("sales.dup_serial")}
                          {dupOf(s).slice(0, 2).map(d => (
                            <div key={d.id} style={{ fontWeight: 400 }}>
                              • {d.promoterName} · {d.saleDate} · {STATUS_VI[d.status] || d.status}
                            </div>
                          ))}
                          {dupOf(s).length > 2 && <div style={{ fontWeight: 400 }}>… +{dupOf(s).length - 2} {t("sales.dup_others")}</div>}
                        </div>
                      )}
                    </td>
                    <td data-label="Highend">{s.isHighend ? <span className="badge badge-blue">Có</span> : <span className="badge badge-gray">Không</span>}</td>
                    <td data-label="Ngày bán">{s.saleDate}</td>
                    <td data-label="">
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => handleApprove(s)}>{t("common.approve_short")}</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleReject(s)}>{t("common.reject_short")}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && tab === "cancel" && (
        <div className="card">
          {cancelReqs.length === 0 ? (
            <div style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("sales.no_cancel_reqs")}</div>
          ) : (
            <table className="table-card">
              <thead><tr><th>{t("sales.promoter")}</th><th>{t("sales.model")}</th><th>{t("sales.serial")}</th><th>{t("x.month")}</th><th>{t("sales.cancel_reason")}</th><th></th></tr></thead>
              <tbody>
                {cancelReqs.map(s => {
                  const locked = !isMonthEditable(s.month);
                  return (
                    <tr key={s.id}>
                      <td data-label="Promoter">{s.promoterName}</td>
                      <td data-label="Model">{s.model}</td>
                      <td data-label="Serial" style={{ fontFamily: "monospace", fontSize: 12 }}>{s.serial}</td>
                      <td data-label="Tháng" style={{ fontSize: 12, color: "#888" }}>{s.month}</td>
                      <td data-label="Lý do" style={{ fontSize: 12 }}>{s.cancelReason || "—"}</td>
                      <td data-label="">
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          {locked ? (
                            <span className="badge badge-gray" title={t("sales.locked_hint")}>{t("sales.locked_badge")}</span>
                          ) : (
                            <button className="btn btn-primary btn-sm" onClick={() => handleApproveCancel(s)}>{t("sales.approve_cancel")}</button>
                          )}
                          <button className="btn btn-sm btn-danger" onClick={() => handleRejectCancel(s)}>{t("sales.reject_cancel")}</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && tab === "detail" && (
        <div className="card">
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <input
              placeholder={t("sales.search_ph")}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 220, fontSize: 13 }}
            />
            <span style={{ fontSize: 12, color: "#888" }}>{sortedDetail.length}/{sales.length} {t("sales.orders_unit")}</span>
            <button className="btn btn-sm" onClick={exportSalesCSV} disabled={sales.length === 0}>
              ⬇ {t("common.export")}
            </button>
          </div>
          <table className="table-card">
            <thead><tr>
              <th style={{ cursor: "pointer" }} onClick={() => sortToggle("promoterName")}>{t("sales.promoter")}{sortArrow("promoterName")}</th>
              <th style={{ cursor: "pointer" }} onClick={() => sortToggle("model")}>{t("sales.model")}{sortArrow("model")}</th>
              <th>{t("sales.serial")}</th>
              <th>{t("sales.highend")}</th>
              <th style={{ cursor: "pointer" }} onClick={() => sortToggle("saleDate")}>{t("sales.sale_date")}{sortArrow("saleDate")}</th>
              <th></th>
            </tr></thead>
            <tbody>
              {sortedDetail.map(s => (
                <tr key={s.id}>
                  <td data-label="Promoter">{s.promoterName}</td>
                  <td data-label="Model">{s.model}</td>
                  <td data-label="Serial" style={{ fontFamily: "monospace", fontSize: 12 }}>{s.serial}</td>
                  <td data-label="Highend">
                    <button
                      className={"badge " + (s.isHighend ? "badge-blue" : "badge-gray")}
                      onClick={() => handleToggleHighend(s)}
                      title={t("sales.toggle_he")}
                      style={{ cursor: "pointer", border: "none" }}
                    >{s.isHighend ? t("sales.he_yes") : t("sales.he_no")}</button>
                  </td>
                  <td data-label="Ngày bán">{s.saleDate}</td>
                  <td data-label="">
                    {isMonthEditable(s.month) && (
                      <button className="btn btn-sm" style={{ fontSize: 11, padding: "2px 6px", color: "#b45309", borderColor: "#fde68a" }}
                        onClick={() => handleRevertApproval(s)} title={t("sales.revert_hint")}>
                        {t("sales.revert")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {sales.length === 0 && <tr><td colSpan={6} style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("common.noData")}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
